import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { KanbanBoard } from '../components/KanbanBoard';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, X, Building2, Mail, Phone, LayoutGrid, List, ChevronRight, User } from 'lucide-react';

const LEAD_COLUMNS = [
  { id: 'contact_leads', title: 'Contact Leads', color: 'amber' },
  { id: 'npi_import', title: 'NPI Registry', color: 'teal' },
  { id: 'website_lead', title: 'Website Leads', color: 'lime' },
  { id: 'new', title: 'New', color: 'blue' },
  { id: 'contacted', title: 'Contacted', color: 'yellow' },
  { id: 'qualified', title: 'Qualified', color: 'purple' },
  { id: 'proposal', title: 'Proposal', color: 'orange' },
];

const PRIORITY_COLORS = {
  low: 'bg-gray-100 text-gray-600',
  medium: 'bg-yellow-100 text-yellow-700',
  high: 'bg-red-100 text-red-700',
};

const STATUS_COLORS = {
  npi_import: 'bg-teal-500 text-white',
  website_lead: 'bg-[#84CC16] text-white',
  new: 'bg-[#014DB7] text-white',
  contacted: 'bg-yellow-500 text-white',
  qualified: 'bg-purple-500 text-white',
  proposal: 'bg-orange-500 text-white',
  won: 'bg-green-600 text-white',
  lost: 'bg-red-500 text-white',
};

const LeadCard = ({ lead, users }) => {
  const assignedUser = users.find(u => u.id === lead.assigned_to);
  
  return (
    <Link 
      to={`/crm/leads/${lead.id}`}
      className="block bg-white rounded-xl p-3 border border-gray-200 shadow-sm hover:shadow-lg hover:border-[#014DB7]/40 transition-all duration-200 group relative"
      data-testid={`lead-card-${lead.id}`}
    >
      {/* Red dot for previously pitched leads */}
      {lead.previously_pitched && (
        <div 
          className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full border-2 border-white shadow-sm flex items-center justify-center"
          title="Previously Pitched"
        >
          <span className="text-white text-[8px] font-bold">!</span>
        </div>
      )}
      
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1 min-w-0 pr-4">
          <h4 className="font-semibold text-[#0A1628] group-hover:text-[#014DB7] transition-colors truncate text-sm">
            {lead.first_name} {lead.last_name}
          </h4>
          {lead.company && (
            <p className="text-xs text-gray-500 flex items-center gap-1 truncate">
              <Building2 className="w-3 h-3 flex-shrink-0" /> 
              <span className="truncate">{lead.company}</span>
            </p>
          )}
        </div>
      </div>
      
      <div className="space-y-0.5 text-xs text-gray-500 mb-2">
        {lead.previously_pitched && (
          <p className="flex items-center gap-1 text-red-500 font-medium">
            <span className="w-2 h-2 bg-red-500 rounded-full"></span>
            Previously Pitched
          </p>
        )}
        <p className="flex items-center gap-1 truncate">
          <Mail className="w-3 h-3 flex-shrink-0" /> 
          <span className="truncate">{lead.email}</span>
        </p>
      </div>

      <div className="flex items-center justify-between gap-2">
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${PRIORITY_COLORS[lead.priority] || PRIORITY_COLORS.medium}`}>
          {lead.priority}
        </span>
        <div className="flex items-center gap-1">
          {assignedUser && (
            <div className="w-5 h-5 bg-[#014DB7] rounded-full flex items-center justify-center flex-shrink-0" title={`${assignedUser.first_name} ${assignedUser.last_name}`}>
              <span className="text-white text-[9px] font-medium">
                {assignedUser.first_name?.[0]}{assignedUser.last_name?.[0]}
              </span>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
};

const LeadListRow = ({ lead, users, onStatusChange }) => {
  const assignedUser = users.find(u => u.id === lead.assigned_to);
  const navigate = useNavigate();
  
  return (
    <div 
      className="flex items-center gap-4 p-4 bg-white border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors group"
      onClick={() => navigate(`/crm/leads/${lead.id}`)}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="w-10 h-10 bg-[#014DB7]/10 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-[#014DB7] text-sm font-semibold">
                {lead.first_name?.[0]}{lead.last_name?.[0]}
              </span>
            </div>
            {lead.previously_pitched && (
              <div 
                className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full border-2 border-white shadow-sm"
                title="Previously Pitched"
              />
            )}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h4 className="font-semibold text-[#0A1628] group-hover:text-[#014DB7] transition-colors truncate">
                {lead.first_name} {lead.last_name}
              </h4>
              {lead.previously_pitched && (
                <span className="text-xs text-red-500 font-medium px-1.5 py-0.5 bg-red-50 rounded">Pitched</span>
              )}
            </div>
            <p className="text-sm text-gray-500 truncate">{lead.email}</p>
          </div>
        </div>
      </div>
      
      <div className="w-40 hidden md:block">
        {lead.company && (
          <p className="text-sm text-gray-600 truncate flex items-center gap-1">
            <Building2 className="w-4 h-4 text-gray-400" />
            {lead.company}
          </p>
        )}
      </div>
      
      <div className="w-28" onClick={(e) => e.stopPropagation()}>
        <Select value={lead.status} onValueChange={(v) => onStatusChange(lead.id, v)}>
          <SelectTrigger className="h-8 text-xs rounded-lg border-0 bg-transparent hover:bg-gray-100">
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[lead.status]}`}>
              {LEAD_COLUMNS.find(c => c.id === lead.status)?.title}
            </span>
          </SelectTrigger>
          <SelectContent>
            {LEAD_COLUMNS.map(col => (
              <SelectItem key={col.id} value={col.id}>
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${STATUS_COLORS[col.id]?.replace('text-white', '')}`} />
                  {col.title}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      <div className="w-20 hidden sm:block">
        <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${PRIORITY_COLORS[lead.priority]}`}>
          {lead.priority}
        </span>
      </div>
      
      <div className="w-10">
        {assignedUser ? (
          <div className="w-8 h-8 bg-[#84CC16] rounded-full flex items-center justify-center" title={`${assignedUser.first_name} ${assignedUser.last_name}`}>
            <span className="text-white text-xs font-medium">
              {assignedUser.first_name?.[0]}{assignedUser.last_name?.[0]}
            </span>
          </div>
        ) : (
          <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
            <User className="w-4 h-4 text-gray-400" />
          </div>
        )}
      </div>
      
      <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-[#014DB7] transition-colors" />
    </div>
  );
};

const AddLeadModal = ({ isOpen, onClose, onSave, users }) => {
  const [formData, setFormData] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    company: '', job_title: '', source: 'manual', status: 'new',
    priority: 'medium', assigned_to: '',
  });

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
    setFormData({
      first_name: '', last_name: '', email: '', phone: '',
      company: '', job_title: '', source: 'manual', status: 'new',
      priority: 'medium', assigned_to: '',
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg">
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-[#0A1628]">Add New Lead</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>First Name *</Label>
              <Input
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                required
                className="rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label>Last Name *</Label>
              <Input
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                required
                className="rounded-xl"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="rounded-xl"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Company</Label>
              <Input
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                className="rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label>Assigned To</Label>
              <Select value={formData.assigned_to} onValueChange={(v) => setFormData({ ...formData, assigned_to: v })}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Select user" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {users.map(user => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.first_name} {user.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-xl">
              Cancel
            </Button>
            <Button type="submit" className="bg-[#014DB7] hover:bg-[#003A8C] text-white rounded-xl">
              Create Lead
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export const LeadsPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState('kanban'); // 'kanban' or 'list'

  const fetchData = async () => {
    try {
      const [leadsRes, usersRes] = await Promise.all([
        api.get('/api/leads'),
        api.get('/api/users'),
      ]);
      setLeads(leadsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleItemMove = async (itemId, newStatus) => {
    setLeads(prev => prev.map(lead => 
      lead.id === itemId ? { ...lead, status: newStatus } : lead
    ));

    try {
      await api.put(`/api/leads/${itemId}`, { status: newStatus });
    } catch (error) {
      toast.error('Failed to update lead');
      fetchData();
    }
  };

  const handleAddLead = async (data) => {
    try {
      const response = await api.post('/api/leads', data);
      toast.success('Lead created');
      setModalOpen(false);
      navigate(`/crm/leads/${response.data.lead.id}`);
    } catch (error) {
      toast.error('Failed to create lead');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-[#014DB7] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0A1628]">Leads</h1>
          <p className="text-gray-500">Manage your sales pipeline</p>
        </div>
        <div className="flex items-center gap-3">
          {/* View Toggle */}
          <div className="flex items-center bg-gray-100 rounded-xl p-1">
            <button
              onClick={() => setViewMode('kanban')}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                viewMode === 'kanban' 
                  ? 'bg-white text-[#014DB7] shadow-sm' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid="kanban-view-btn"
            >
              <LayoutGrid className="w-4 h-4" />
              Kanban
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                viewMode === 'list' 
                  ? 'bg-white text-[#014DB7] shadow-sm' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid="list-view-btn"
            >
              <List className="w-4 h-4" />
              List
            </button>
          </div>
          
          <Button onClick={() => setModalOpen(true)} className="bg-[#014DB7] hover:bg-[#003A8C] text-white rounded-xl">
            <Plus className="w-4 h-4 mr-2" /> Add Lead
          </Button>
        </div>
      </div>

      {viewMode === 'kanban' ? (
        <KanbanBoard
          columns={LEAD_COLUMNS}
          items={leads}
          onItemMove={handleItemMove}
          renderItem={(lead) => (
            <LeadCard lead={lead} users={users} />
          )}
        />
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          {/* List Header */}
          <div className="flex items-center gap-4 px-4 py-3 bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase tracking-wider">
            <div className="flex-1">Lead</div>
            <div className="w-40 hidden md:block">Company</div>
            <div className="w-28">Status</div>
            <div className="w-20 hidden sm:block">Priority</div>
            <div className="w-10">Assigned</div>
            <div className="w-5"></div>
          </div>
          
          {/* List Rows */}
          <div className="divide-y divide-gray-100">
            {leads.length === 0 ? (
              <div className="p-8 text-center text-gray-500">No leads found</div>
            ) : (
              leads.map(lead => (
                <LeadListRow 
                  key={lead.id} 
                  lead={lead} 
                  users={users}
                  onStatusChange={handleItemMove}
                />
              ))
            )}
          </div>
        </div>
      )}

      <AddLeadModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleAddLead}
        users={users}
      />
    </div>
  );
};

export default LeadsPage;
